import IcDelete from '@assets/details-camera-icons/IcDelete';
import IcEdit from '@assets/details-camera-icons/IcEdit';
import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { GeoZoneType } from '@EcamModel/model';
import { CameraView } from '@EcamModel/model/CameraView';
import useMenuPosition from '@hooks/useMenuPosition';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { BaseGeoZonesDetail } from '@pages/cameras/details';
import { getCenterFrame } from '@pages/cameras/details/utils';
import { DangerTooltip } from '@pages/cameras/list/components/TooltipDangerIconButton';
import color from '@theme/Colors';
import React from 'react';
import { formatUKDate, formatUKTime } from 'src/helpers';
import { useCameraDetailContext } from '../../../../hook/CameraDetailsProvider';
import { typeTextMap } from '../../../../hook/GeoZonesNavigateHandler';
import { DetailsGroup, DetailsInformation } from '../../CameraInformation';

interface StyledMenuProps {
    frame: {
        x: number;
        y: number;
        geoZoneId: number | undefined;
    }[];
    frameIndex: number;
    listCameraView: CameraView;
    containerSize: {
        width: number;
        height: number;
    };
}

const mappingGeoZonesType = {
    [GeoZoneType.Detail]: 'Detail',
    [GeoZoneType.Overview]: 'Overview',
};

const StyledMenu: React.FC<StyledMenuProps> = ({ frame, frameIndex, listCameraView, containerSize }) => {
    const { setSelectedGeozone, setSelectedCameraView } = useCameraDetailContext();
    const center = getCenterFrame(frame);
    const { menuRef, menuPosition } = useMenuPosition({ center, containerSize });

    const matchingGeoZone =
        frame && frame.length > 0 ? listCameraView?.GeoZones?.find((gz) => gz?.Id === frame[0]?.geoZoneId) : undefined;

    const filterMapping: { [key in GeoZoneType]: (item: DetailsInformation) => boolean } = {
        [GeoZoneType.Overview]: (item) => item.id !== 3 && item.id !== 6 && item.id !== 7,
        [GeoZoneType.Detail]: (item) => item.id !== 2,
    };

    const formatUserInfo = (userEmail?: string, date?: string, time?: string): string => {
        const user = userEmail?.replace('@ukparkingcontrol.com', '') ?? '';
        return `${user} at ${date} on ${time}`;
    };

    const combinedDetailGeoZones: DetailsInformation[] = [
        {
            id: 1,
            label: 'Type',
            value: mappingGeoZonesType[matchingGeoZone?.GeoZoneType!],
            groupDetail: DetailsGroup.GENERAL,
        },
        {
            id: 2,
            label: 'Geo-zone details',
            value: matchingGeoZone?.GeoZoneDetailCount ?? 0,
            groupDetail: DetailsGroup.GENERAL,
        },
        {
            id: 3,
            label: 'Contravention reason',
            value: matchingGeoZone?.ContraventionReason?.ContraventionReasonTranslations?.[0]?.Summary,
            groupDetail: DetailsGroup.GENERAL,
        },
        {
            id: 4,
            label: 'Created by',
            value: formatUserInfo(
                matchingGeoZone?.CreatedBy,
                formatUKDate(matchingGeoZone?.Created!),
                formatUKTime(matchingGeoZone?.Created!)
            ),
            groupDetail: DetailsGroup.CREATED,
        },
        {
            id: 5,
            label: 'Updated by',
            value: formatUserInfo(
                matchingGeoZone?.UpdatedBy,
                formatUKDate(matchingGeoZone?.Updated!),
                formatUKTime(matchingGeoZone?.Updated!)
            ),
            groupDetail: DetailsGroup.UPDATED,
        },
        {
            id: 6,
            label: 'Name',
            value: matchingGeoZone?.GeoZoneName ?? 'N/A',
            groupDetail: DetailsGroup.GENERAL,
        },
        {
            id: 7,
            label: 'Total Bay',
            value: matchingGeoZone?.TotalBay ?? 0,
            groupDetail: DetailsGroup.GENERAL,
        },
    ];

    const filteredDetailGeoZones = combinedDetailGeoZones.filter(filterMapping[matchingGeoZone?.GeoZoneType! ?? 0]);

    const { popUpDeleteGeoZonesOverview, navigate } = useCameraDetailContext();

    return (
        <Stack
            ref={menuRef}
            width={'max-content'}
            sx={{
                position: 'absolute',
                top: `${menuPosition?.top}px`,
                left: `${menuPosition?.left}px`,
                display: menuPosition ? 'block' : 'none',
                zIndex: 10,
                backgroundColor: 'white',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
                padding: '12px',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
            }}
        >
            {frame && frame.length > 0 && (
                <Stack
                    key={frame[0]?.geoZoneId}
                    direction={'row'}
                    justifyContent={'space-between'}
                    width={'100%'}
                    alignItems={'center'}
                >
                    <Stack width={'100%'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography
                            variant="h5"
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { color: color.priText },
                            }}
                            onClick={() => {
                                if (matchingGeoZone?.GeoZoneType === GeoZoneType.Overview) {
                                    matchingGeoZone?.CameraViewDetail &&
                                        setSelectedCameraView(matchingGeoZone.CameraViewDetail);
                                    matchingGeoZone && setSelectedGeozone(matchingGeoZone);

                                    return;
                                }

                                navigate(`edit/${frame[0]?.geoZoneId}/${typeTextMap[CameraType.PTZ]}`, {
                                    state: {
                                        geoZonesItem: matchingGeoZone,
                                        frameIndex: frameIndex,
                                        cameraView: listCameraView,
                                    },
                                    replace: true,
                                });
                            }}
                        >
                            {matchingGeoZone?.GeoZoneType === GeoZoneType.Overview
                                ? matchingGeoZone?.GeoZoneName || matchingGeoZone?.CameraViewDetail?.CameraViewName
                                : matchingGeoZone?.GeoZoneName || `Geo-zone ${frameIndex + 1}`}
                        </Typography>
                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                            <Tooltip title="Edit" placement="top">
                                <IconButton
                                    size="small"
                                    sx={{
                                        backgroundColor: color.grey200,
                                        border: `6px solid ${color.grey100}`,
                                        width: '32px',
                                        height: '32px',
                                        padding: '1px',
                                        mb: '1px',
                                    }}
                                    onClick={() => {
                                        navigate(`edit/${frame[0]?.geoZoneId}/${typeTextMap[CameraType.PTZ]}`, {
                                            state: {
                                                geoZonesItem: matchingGeoZone,
                                                cameraView: listCameraView,
                                            },
                                            replace: true,
                                        });
                                    }}
                                >
                                    <IcEdit
                                        style={{
                                            width: '15px',
                                            height: '15px',
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>

                            <DangerTooltip title="Delete" placement="top">
                                <Stack>
                                    <IconButton
                                        size="small"
                                        sx={{
                                            backgroundColor: color.grey200,
                                            border: `6px solid ${color.grey100}`,
                                            width: '32px',
                                            height: '32px',
                                            padding: '2px',
                                            ml: '-4px',
                                            mb: '2px',
                                        }}
                                        onClick={() => {
                                            setSelectedGeozone(matchingGeoZone);
                                            popUpDeleteGeoZonesOverview.setTrue();
                                        }}
                                    >
                                        <IcDelete
                                            style={{
                                                width: '14px',
                                                height: '14px',
                                                color: color.danger,
                                            }}
                                        />
                                    </IconButton>
                                </Stack>
                            </DangerTooltip>
                        </Stack>
                    </Stack>
                </Stack>
            )}

            <Stack direction="column" spacing="2px" mt={1}>
                {[DetailsGroup.GENERAL, DetailsGroup.CREATED, DetailsGroup.UPDATED].map((group) => (
                    <BaseGeoZonesDetail
                        key={group}
                        detailGeoZones={filteredDetailGeoZones.filter((item) => item.groupDetail === group)}
                    />
                ))}
            </Stack>
        </Stack>
    );
};

export default StyledMenu;
